import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiDownload,
  FiMessageSquare,
  FiTrash2,
  FiUsers,
  FiEye,
  FiEyeOff,
  FiHeart,
  FiEdit2,
  FiSave,
  FiX,
} from "react-icons/fi";
import styles from "./DettaglioComunicazione.module.css";
import { API_BASE } from "../api";

const API = API_BASE;

async function fetchOrThrow(input, init) {
  const res = await fetch(input, init);
  if (!res.ok) {
    const urlTxt = typeof input === "string" ? input : input?.toString?.() || "";
    let msg = `HTTP ${res.status} @ ${urlTxt}`;
    try {
      const j = await res.json();
      if (j?.error) msg += ` — ${j.error}`;
    } catch {}
    throw new Error(msg);
  }
  return res;
}

export default function DettaglioComunicazione({ canDelete = true }) {
  const { id: rawId } = useParams();
  const navigate = useNavigate();

  const id = Number(rawId);
  const idValido = Number.isInteger(id) && id > 0;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState("info"); // info | destinatari | letti | non_letti | likes | comments

  const [editing, setEditing] = useState(false);
  const [editTitolo, setEditTitolo] = useState("");
  const [editContenuto, setEditContenuto] = useState("");
  const [editNotify, setEditNotify] = useState("none"); // "both" | "push" | "none"
  const [saving, setSaving] = useState(false);

  const loadAdminDetail = useCallback(async () => {
    if (!idValido) return;
    setLoading(true);
    try {
      const res = await fetchOrThrow(`${API}/comunicazioni/${id}/admin`, { credentials: "include" });
      const j = await res.json();
      setData(j);
    } catch (e) {
      alert(e.message || "Errore caricamento comunicazione");
      navigate("/comunicazioni");
    } finally {
      setLoading(false);
    }
  }, [id, idValido, navigate]);

  useEffect(() => {
    if (!idValido) {
      alert("Comunicazione non trovata (ID non valido).");
      navigate("/comunicazioni");
      return;
    }
    loadAdminDetail();
  }, [rawId, idValido, navigate, loadAdminDetail]);

  const handleDelete = async () => {
    const ok = window.confirm("Eliminare questa comunicazione?");
    if (!ok) return;
    try {
      await fetchOrThrow(`${API}/comunicazioni/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      alert("Comunicazione eliminata");
      navigate("/comunicazioni");
    } catch (e) {
      alert(e?.message || "Errore eliminazione");
    }
  };

  const startEditing = () => {
    setEditTitolo(data?.comunicazione?.titolo || "");
    setEditContenuto(data?.comunicazione?.contenuto || "");
    setEditNotify("none");
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const handleSaveEdit = async () => {
    const titolo = editTitolo.trim();
    const contenuto = editContenuto.trim();
    if (!titolo || !contenuto) {
      alert("Titolo e contenuto sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      const res = await fetchOrThrow(`${API}/comunicazioni/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ titolo, contenuto, notify: editNotify }),
      });
      const updated = await res.json();
      setData((prev) => ({ ...prev, comunicazione: { ...prev.comunicazione, ...updated } }));
      setEditing(false);
    } catch (e) {
      alert(e?.message || "Errore modifica comunicazione");
    } finally {
      setSaving(false);
    }
  };

  if (!idValido) return null;
  if (loading || !data) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Comunicazione</h2>
          <button
            type="button"
            className={`btn btn-outline ${styles.backBtn}`}
            onClick={() => navigate("/comunicazioni")}
          >
            <FiArrowLeft /> Indietro
          </button>
        </div>
        <div className={styles.underline} />
        <p>Caricamento…</p>
      </div>
    );
  }

  const comm = data.comunicazione;
  const pubDate = comm.data_pubblicazione
    ? new Date(comm.data_pubblicazione).toLocaleString()
    : "";
  // 🔥 prima prova nuovi allegati (tabella comunicazione_attachments)
  const firstAttachment =
    Array.isArray(data.attachments) && data.attachments.length
      ? data.attachments[0]
      : null;

  // fallback legacy
  const hasAttachment = !!firstAttachment || !!comm.allegato_url;
  const previewUrl = hasAttachment ? `${API}/comunicazioni/${id}/view` : null;
  const downloadUrl = hasAttachment ? `${API}/comunicazioni/${id}/download` : null;

  const fileName = (firstAttachment?.file_url || comm.allegato_url || "").toLowerCase();
  const isPdf = fileName.endsWith(".pdf") || (firstAttachment?.mime_type === "application/pdf");

  return (
    <div className={styles.container}>
      {/* Header: titolo a sx + back a dx */}
      <div className={styles.header}>
        <h2 className={styles.title}>{comm.titolo || "Comunicazione"}</h2>
        <button
          type="button"
          className={`btn btn-outline ${styles.backBtn}`}
          onClick={() => navigate("/comunicazioni")}
        >
          <FiArrowLeft /> Indietro
        </button>
      </div>
      <div className={styles.underline} />

      {/* Tabs */}
      <div className={styles.tabs}>
        <button className={tab === "info" ? styles.tabActive : styles.tab} onClick={() => setTab("info")}>
          Info
        </button>
        <button className={tab === "destinatari" ? styles.tabActive : styles.tab} onClick={() => setTab("destinatari")}>
          <FiUsers /> Destinatari ({data.destinatari.length})
        </button>
        <button className={tab === "letti" ? styles.tabActive : styles.tab} onClick={() => setTab("letti")}>
          <FiEye /> Letti ({data.letti.length})
        </button>
        <button className={tab === "non_letti" ? styles.tabActive : styles.tab} onClick={() => setTab("non_letti")}>
          <FiEyeOff /> Non letti ({data.non_letti.length})
        </button>
        <button className={tab === "likes" ? styles.tabActive : styles.tab} onClick={() => setTab("likes")}>
          <FiHeart /> Like ({data.likes.length})
        </button>
        <button className={tab === "comments" ? styles.tabActive : styles.tab} onClick={() => setTab("comments")}>
          <FiMessageSquare /> Commenti ({data.comments.length})
        </button>
      </div>

      {/* Contenuti tab */}
      {tab === "info" && (
        <div className={styles.card}>
          <div className={styles.metaRow}>
            <span className={styles.metaBadge}>{pubDate}</span>
            <span className={styles.metaBadge}>
              <FiMessageSquare /> {data.comments.length}
            </span>
            <span className={styles.metaBadge}>
              <FiHeart /> {data.likes.length}
            </span>
          </div>

          {editing ? (
            <div className={styles.editForm}>
              <div className={styles.group}>
                <label className={styles.label} htmlFor="editTitolo">Titolo</label>
                <input
                  id="editTitolo"
                  type="text"
                  className={styles.input}
                  value={editTitolo}
                  onChange={(e) => setEditTitolo(e.target.value)}
                />
              </div>
              <div className={styles.group}>
                <label className={styles.label} htmlFor="editContenuto">Contenuto</label>
                <textarea
                  id="editContenuto"
                  className={styles.textarea}
                  rows={6}
                  value={editContenuto}
                  onChange={(e) => setEditContenuto(e.target.value)}
                />
              </div>
              <div className={styles.group}>
                <label className={styles.label}>Dopo il salvataggio</label>
                <div className={styles.notifyOptions}>
                  <label className={styles.notifyOption}>
                    <input
                      type="radio"
                      name="editNotify"
                      value="both"
                      checked={editNotify === "both"}
                      onChange={() => setEditNotify("both")}
                    />
                    Invia una nuova email e una notifica push
                  </label>
                  <label className={styles.notifyOption}>
                    <input
                      type="radio"
                      name="editNotify"
                      value="push"
                      checked={editNotify === "push"}
                      onChange={() => setEditNotify("push")}
                    />
                    Invia solo la notifica push
                  </label>
                  <label className={styles.notifyOption}>
                    <input
                      type="radio"
                      name="editNotify"
                      value="none"
                      checked={editNotify === "none"}
                      onChange={() => setEditNotify("none")}
                    />
                    Nessuna delle due (modifica solo il testo in app)
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.content}>{comm.contenuto || ""}</div>
          )}

          {/* Allegato */}
          {hasAttachment && (
            <div className={styles.attachWrap}>
              <p className={styles.attachTitle}>Allegato</p>
              <div className={styles.attachBox}>
                {isPdf ? (
                  <iframe
                    title="allegato"
                    src={previewUrl}
                    style={{ width: "100%", height: 520, border: "none", display: "block" }}
                  />
                ) : (
                  <img
                    alt="allegato"
                    src={previewUrl}
                    style={{ width: "100%", display: "block", objectFit: "contain" }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Azioni admin */}
          <div className={styles.actions}>
            {hasAttachment && (
              <a className={styles.btnPrimary} href={downloadUrl}>
                <FiDownload /> Scarica allegato
              </a>
            )}
            {editing ? (
              <>
                <button
                  type="button"
                  className={styles.btnOutline}
                  onClick={cancelEditing}
                  disabled={saving}
                >
                  <FiX /> Annulla
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={handleSaveEdit}
                  disabled={saving}
                >
                  <FiSave /> {saving ? "Salvataggio..." : "Salva modifiche"}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.btnOutline}
                onClick={startEditing}
                title="Modifica testo comunicazione"
              >
                <FiEdit2 /> Modifica
              </button>
            )}
            {canDelete && !editing && (
              <button
                type="button"
                className={styles.btnDanger}
                onClick={handleDelete}
                title="Elimina comunicazione"
              >
                <FiTrash2 /> Elimina
              </button>
            )}
          </div>
        </div>
      )}

      {tab === "destinatari" && (
        <div className={styles.card}>
          <h3>Destinatari</h3>
          <ul className={styles.destList}>
            {data.destinatari.map((u) => (
              <li key={u.id} className={styles.destItem}>
                {u.nome} {u.cognome} — {u.email}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "letti" && (
        <div className={styles.card}>
          <h3>Letti</h3>
          <ul className={styles.destList}>
            {data.letti.map((u) => (
              <li key={u.utente_id} className={styles.destItem}>
                {u.nome} {u.cognome} — {u.email} • {new Date(u.data_lettura).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "non_letti" && (
        <div className={styles.card}>
          <h3>Non letti</h3>
          <ul className={styles.destList}>
            {data.non_letti.map((u) => (
              <li key={u.id} className={styles.destItem}>
                {u.nome} {u.cognome} — {u.email}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "likes" && (
        <div className={styles.card}>
          <h3>Like</h3>
          <ul className={styles.destList}>
            {data.likes.map((u, i) => (
              <li key={`${u.utente_id}-${i}`} className={styles.destItem}>
                {u.nome} {u.cognome} — {u.email}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === "comments" && (
        <div className={styles.card}>
          <h3>Commenti</h3>
          <ul className={styles.commentsList}>
            {data.comments.map((c) => (
              <li key={c.id} className={styles.commentItem}>
                <div className={styles.commentMeta}>
                  {c.nome} {c.cognome} • {new Date(c.created_at).toLocaleString()}
                </div>
                <div className={styles.commentBody}>{c.contenuto}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
